import React from 'react';
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Bot, Sparkles, Target, Zap, Plus, Trash2, Edit2, Check, X } from 'lucide-react';

export default function BotPersonalitySection({ profile, canEdit, onUpdate }) {
  const [editingField, setEditingField] = React.useState(null);
  const [editValue, setEditValue] = React.useState('');
  const [newItem, setNewItem] = React.useState({ field: null, value: '' });

  const personality = profile?.bot_personality || {};

  const handleSaveField = (field, value) => {
    onUpdate({
      bot_personality: {
        ...personality,
        [field]: value
      }
    });
    setEditingField(null);
    setEditValue('');
  };

  const handleAddArrayItem = (field) => {
    if (!newItem.value.trim()) return;
    const current = personality[field] || [];
    onUpdate({
      bot_personality: {
        ...personality,
        [field]: [...current, newItem.value]
      }
    });
    setNewItem({ field: null, value: '' });
  };

  const handleRemoveArrayItem = (field, index) => {
    const current = personality[field] || [];
    onUpdate({
      bot_personality: {
        ...personality,
        [field]: current.filter((_, i) => i !== index)
      }
    });
  };

  const handleEditArrayItem = (field, index, value) => {
    const current = personality[field] || [];
    const updated = [...current];
    updated[index] = value;
    onUpdate({
      bot_personality: {
        ...personality,
        [field]: updated
      }
    });
    setEditingField(null);
    setEditValue('');
  };

  const renderArrayField = (field, label, icon, color) => {
    const items = personality[field] || [];
    const Icon = icon;
    
    return (
      <div>
        <div className="flex items-center gap-2 mb-3">
          <Icon className={`w-4 h-4 ${color}`} />
          <h4 className="text-slate-700 text-sm font-semibold uppercase tracking-wider">{label}</h4>
        </div>
        <div className="space-y-2">
          {items.map((item, index) => (
            <div key={index} className="bg-slate-100 p-2 rounded-lg flex items-center gap-2 border border-slate-800">
              {editingField === `${field}-${index}` ? (
                <>
                  <Input data-testid="profile-botpersonalitysection-input"
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    className="flex-1 bg-slate-950 border-slate-700 h-8"
                    autoFocus
                  />
                  <Button size="icon" variant="ghost" onClick={() => handleEditArrayItem(field, index, editValue)} className="h-8 w-8 text-emerald-400">
                    <Check className="w-4 h-4" />
                  </Button>
                  <Button size="icon" variant="ghost" onClick={() => setEditingField(null)} className="h-8 w-8 text-slate-500">
                    <X className="w-4 h-4" />
                  </Button>
                </>
              ) : (
                <>
                  <span className="text-slate-700 text-sm flex-1">{item}</span>
                  {canEdit && (
                    <>
                      <Button size="icon" variant="ghost" onClick={() => { setEditingField(`${field}-${index}`); setEditValue(item); }} className="h-8 w-8 text-slate-500 hover:text-indigo-400">
                        <Edit2 className="w-3.5 h-3.5" />
                      </Button>
                      <Button size="icon" variant="ghost" onClick={() => handleRemoveArrayItem(field, index)} className="h-8 w-8 text-slate-500 hover:text-rose-400">
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </>
                  )}
                </>
              )}
            </div>
          ))}
          {canEdit && newItem.field === field ? (
            <div className="flex items-center gap-2">
              <Input data-testid="profile-botpersonalitysection-input-1"
                value={newItem.value}
                onChange={(e) => setNewItem({ ...newItem, value: e.target.value })}
                placeholder={`Add ${label.toLowerCase()}...`}
                className="flex-1 bg-slate-50 border-slate-700 h-8"
                autoFocus
                onKeyPress={(e) => e.key === 'Enter' && handleAddArrayItem(field)}
              />
              <Button size="sm" onClick={() => handleAddArrayItem(field)} className="h-8 bg-indigo-600 hover:bg-indigo-700">Add</Button>
              <Button size="sm" variant="ghost" onClick={() => setNewItem({ field: null, value: '' })} className="h-8">Cancel</Button>
            </div>
          ) : canEdit && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setNewItem({ field, value: '' })}
              className="w-full border-dashed border-slate-700 text-slate-400 hover:text-slate-200"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add {label}
            </Button>
          )}
        </div>
      </div>
    );
  };

  return (
    <Card className="glass-panel border-none">
      <CardHeader>
        <CardTitle className="text-slate-700 font-semibold tracking-tight leading-none flex items-center gap-2">
          <Bot className="w-5 h-5 text-purple-400" />
          Bot Personality
        </CardTitle>
        <p className="text-xs text-slate-500 mt-2">Defines how this AI Officer behaves and operates</p>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Name & Specialty */}
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs text-slate-500 uppercase tracking-wider mb-1 block">Name</label>
            {editingField === 'name' ? (
              <div className="flex items-center gap-2">
                <Input data-testid="profile-botpersonalitysection-input-2"
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  className="flex-1 bg-slate-950 border-slate-700 h-8"
                  autoFocus
                />
                <Button size="icon" variant="ghost" onClick={() => handleSaveField('name', editValue)} className="h-8 w-8 text-emerald-400">
                  <Check className="w-4 h-4" />
                </Button>
                <Button size="icon" variant="ghost" onClick={() => setEditingField(null)} className="h-8 w-8 text-slate-500">
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <div className="bg-slate-100 p-2 rounded-lg flex items-center gap-2 border border-slate-800">
                <span className="text-slate-700 text-sm flex-1">{personality.name || 'Not set'}</span>
                {canEdit && (
                  <Button size="icon" variant="ghost" onClick={() => { setEditingField('name'); setEditValue(personality.name || ''); }} className="h-8 w-8 text-slate-500 hover:text-indigo-400">
                    <Edit2 className="w-3.5 h-3.5" />
                  </Button>
                )}
              </div>
            )}
          </div>
          <div>
            <label className="text-xs text-slate-500 uppercase tracking-wider mb-1 block">Specialty</label>
            {editingField === 'specialty' ? (
              <div className="flex items-center gap-2">
                <Input data-testid="profile-botpersonalitysection-input-3"
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  className="flex-1 bg-slate-950 border-slate-700 h-8"
                  autoFocus
                />
                <Button size="icon" variant="ghost" onClick={() => handleSaveField('specialty', editValue)} className="h-8 w-8 text-emerald-400">
                  <Check className="w-4 h-4" />
                </Button>
                <Button size="icon" variant="ghost" onClick={() => setEditingField(null)} className="h-8 w-8 text-slate-500">
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <div className="bg-slate-100 p-2 rounded-lg flex items-center gap-2 border border-slate-800">
                <span className="text-slate-700 text-sm flex-1">{personality.specialty || 'Not set'}</span>
                {canEdit && (
                  <Button size="icon" variant="ghost" onClick={() => { setEditingField('specialty'); setEditValue(personality.specialty || ''); }} className="h-8 w-8 text-slate-500 hover:text-indigo-400">
                    <Edit2 className="w-3.5 h-3.5" />
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Personality Traits */}
        {renderArrayField('personality_traits', 'Personality Traits', Sparkles, 'text-amber-400')}

        {/* Strengths */}
        {renderArrayField('strengths', 'Strengths', Zap, 'text-cyan-400')}

        {/* Priorities */}
        {renderArrayField('priorities', 'Priorities', Target, 'text-purple-400')}
      </CardContent>
    </Card>
  );
}